/**
 * Shared "Quick Log" form fields: follow-up shortcuts + progress notes +
 * Time Spent. Presentational and fully controlled — the host owns state and
 * submission.
 *
 * Reused by `QuickTrackModal` (the TaskCard "Track" affordance) and by
 * `AdministrativeQuickLogProgressContent` (the "Log Progress" surface for
 * administrative care plan tasks) so the lightweight log-time-and-a-note
 * experience has one source of truth.
 *
 * @agentic connections
 */

import {
  Group,
  Stack,
  Text,
  TextInput,
  Textarea,
  UnstyledButton,
} from "@mantine/core";
import { addBusinessDays } from "@benchmark/core/utils";
import { CareTimeInlineFields } from "./CareTimeInlineFields";

/** Follow-up shortcut options for a Quick Log entry. @agentic connections */
export type FollowUpKind =
  "none" | "1-day" | "2-days" | "3-days" | "next-week" | "custom";

/**
 * The user's local calendar "today" represented at UTC midnight. Anchoring on
 * the local Y/M/D means subsequent UTC date math (and `addBusinessDays`, which
 * keys off `getUTCDay`) operates on the calendar day the user actually sees —
 * avoiding the off-by-one skew that occurs in negative-UTC-offset timezones
 * when the UTC date has already rolled to tomorrow.
 */
const localCivilTodayUtc = (): Date => {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
};

/** ISO (yyyy-mm-dd) date `days` calendar days from today. @agentic connections */
export const todayPlusDays = (days: number): string => {
  const d = localCivilTodayUtc();
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/**
 * ISO (yyyy-mm-dd) date `days` business days (Mon-Fri) from today; weekends
 * are skipped, so 1 business day from a Friday lands on the next Monday.
 * @agentic connections
 */
export const todayPlusBusinessDays = (days: number): string =>
  addBusinessDays(localCivilTodayUtc(), days).toISOString().slice(0, 10);

/**
 * Resolve a `FollowUpKind` + custom date into the concrete follow-up date
 * (or `undefined` for "none"). The "1 day" / "2 days" / "3 days" shortcuts
 * snooze by business days; "Next week" is a flat +7 calendar days.
 * @agentic connections
 */
export const resolveFollowUpDate = (
  kind: FollowUpKind,
  customDate: string,
): string | undefined => {
  switch (kind) {
    case "none":
      return undefined;
    case "1-day":
      return todayPlusBusinessDays(1);
    case "2-days":
      return todayPlusBusinessDays(2);
    case "3-days":
      return todayPlusBusinessDays(3);
    case "next-week":
      return todayPlusDays(7);
    case "custom":
      return customDate || undefined;
  }
};

/** Short, human-readable rendering of an ISO date. @agentic connections */
export const formatIsoDate = (iso: string): string => {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  return new Date(ms).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

/** Props for `QuickLogFields`. @agentic connections */
export type QuickLogFieldsProps = {
  followUpKind: FollowUpKind;
  onFollowUpKindChange: (kind: FollowUpKind) => void;
  customDate: string;
  onCustomDateChange: (value: string) => void;
  notes: string;
  onNotesChange: (value: string) => void;
  minutes: number | undefined;
  onMinutesChange: (value: number | undefined) => void;
  disabled?: boolean;
};

/**
 * Controlled Quick Log fields: follow-up shortcuts, progress notes, and a
 * single Time Spent input.
 * @agentic connections
 */
export const QuickLogFields = ({
  followUpKind,
  onFollowUpKindChange,
  customDate,
  onCustomDateChange,
  notes,
  onNotesChange,
  minutes,
  onMinutesChange,
  disabled,
}: QuickLogFieldsProps) => {
  const followUpDate = resolveFollowUpDate(followUpKind, customDate);

  const followUpButton = (kind: FollowUpKind, label: string) => (
    <UnstyledButton
      type="button"
      onClick={() => onFollowUpKindChange(kind)}
      px="sm"
      py={6}
      style={{
        border: "1px solid var(--mantine-color-gray-3)",
        borderRadius: 999,
        backgroundColor:
          followUpKind === kind ? "var(--mantine-color-purple-1)" : "white",
        color:
          followUpKind === kind
            ? "var(--mantine-color-purple-7)"
            : "var(--mantine-color-gray-7)",
        fontSize: "0.875rem",
        fontWeight: followUpKind === kind ? 600 : 500,
      }}
    >
      {label}
    </UnstyledButton>
  );

  return (
    <Stack gap="md">
      <Stack gap="xs">
        <Text size="sm" fw={500}>
          Follow up (optional)
        </Text>
        <Group gap="xs" wrap="wrap">
          {followUpButton("1-day", "1 day")}
          {followUpButton("2-days", "2 days")}
          {followUpButton("3-days", "3 days")}
          {followUpButton("next-week", "Next week")}
          {followUpButton("custom", "Custom")}
        </Group>
        {followUpKind === "custom" ? (
          <TextInput
            type="date"
            value={customDate}
            onChange={(e) => onCustomDateChange(e.currentTarget.value)}
            size="sm"
            aria-label="Custom follow-up date"
            disabled={disabled}
          />
        ) : followUpDate ? (
          <Text size="xs" c="dimmed">
            Snoozes until {formatIsoDate(followUpDate)}.
          </Text>
        ) : null}
      </Stack>

      <Textarea
        label="Progress Notes"
        placeholder="Brief update on patient status…"
        value={notes}
        onChange={(e) => onNotesChange(e.currentTarget.value)}
        autosize
        minRows={3}
        disabled={disabled}
      />

      <CareTimeInlineFields
        minutes={minutes}
        onMinutesChange={onMinutesChange}
        notes=""
        onNotesChange={() => {
          // Notes captured in the Progress Notes field above.
        }}
        label="Time Spent"
        helperText="Billable time for this interaction"
        showNotes={false}
        disabled={disabled}
      />
    </Stack>
  );
};
